import { useState, useEffect, lazy, Suspense } from 'react';
// Each page downloads the first time it is opened, not all up front, so the
// login screen appears quickly.
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Typography } from '@mui/material';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AutoLogin from './pages/AutoLogin';
import AdminLayout from './components/AdminLayout';
import DealerLayout from './components/DealerLayout';
import ProtectedRoute from './components/ProtectedRoute';
import TasksType from './theme/TasksType';
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const DashboardV2 = lazy(() => import('./pages/admin/DashboardV2'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
//import ShipmentManagement from './pages/admin/ShipmentManagement';
//import Notifications from './pages/admin/Notifications';
const UpdateManagement = lazy(() => import('./pages/admin/UpdateManagement'));
const Permissions = lazy(() => import('./pages/admin/Permissions'));
const EmailNotification = lazy(() => import('./pages/admin/EmailNotification'));
const ReloadPage = () => {
  window.location.reload();
  return null;
};
const ExternalRedirect = ({ to }: { to: string }) => {
  window.location.replace(to);
  return null;
};
const Calculator = lazy(() => import('./pages/admin/Calculator'));
const TicketHub = lazy(() => import('./pages/admin/TicketHub'));
const LimaSign = lazy(() => import('./pages/admin/LimaSign'));
const Tasks = lazy(() => import('./pages/admin/Tasks'));
const EmailBuilder = lazy(() => import('./pages/admin/EmailBuilder'));
const EmailPreview = lazy(() => import('./pages/admin/EmailPreview'));

// User Pages

// Public Pages

// Dealer Pages
const DealerDashboard = lazy(() => import('./pages/dealer/DealerDashboard'));
const DealerTrackShipments = lazy(() => import('./pages/dealer/DealerTrackShipments'));
const DealerBookShipment = lazy(() => import('./pages/dealer/DealerBookShipment'));
const DealerCalculator = lazy(() => import('./pages/dealer/DealerCalculator'));
const DealerIframeBuilder = lazy(() => import('./pages/dealer/DealerIframeBuilder'));
const DealerResources = lazy(() => import('./pages/dealer/DealerResources'));


// Types
import { UserRole } from './types/user';

// Auth services
import { isAuthenticated, getCurrentUser } from './services/auth';
import { handleAuthError } from './utils/authErrorHandler';
import cachedApiService from './services/apiWithCache';

// The portal's blue is the Tasks page's blue (#2b6cf6), the same one accounting uses,
// so the menu, buttons and links are one colour across the sites. It used to be
// Material's default (#1976d2), which read darker beside them.
const theme = createTheme({
  palette: {
    primary: {
      main: '#2b6cf6',
      dark: '#1f57d1',
      light: '#e8f0fe',
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// A real page load, because /user is a separate page rather than a route here.
const GoToClientPortal = () => {
  const { pathname, search } = window.location;
  window.location.replace(`${pathname.startsWith('/user/') ? pathname : '/user/'}${search}`);
  return null;
};

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isAuthenticated()) {
          // Ask the server on every page load, not the stored copy, so a sign-in that
          // has ended never shows the signed-in screens or their saved data.
          const userData = await getCurrentUser(true);
          if (userData) {
            console.log('App.tsx: User data loaded:', userData);
            console.log('App.tsx: User role:', userData.role);
            setUser(userData);
            setUserRole(userData.role);
            
            // Initialize the cached API service
            await cachedApiService.initialize();
          } else {
            setUser(null);
            setUserRole(null);
          }
        } else {
          setUser(null);
          setUserRole(null);
        }
      } catch (error: any) {
        console.error('Auth check error:', error);
        // Handle authentication errors during initial check
        await handleAuthError(error, false); // Don't reload immediately
        setUser(null);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
    
    // Cleanup on component unmount
    return () => {
      cachedApiService.cleanup();
    };
  }, []);

  // While the sign-in is checked, a blank page on the normal background: no menu,
  // no layout, no saved shipments.
  if (loading) {
    return <div style={{ position: 'fixed', inset: 0, background: '#f3f5fb' }} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={null}>
      <Routes>
        {/* Public routes (no auth) */}
        {/* Shared shipment links are the client portal's page now (see netlify.toml);
            a move to one from inside the app becomes a real page load. */}
        <Route path="/p/:shipmentId" element={<ReloadPage />} />

        {/* Staff sign in here. /user/login is the client and dealer portal's own sign-in. */}
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
        <Route path="/register" element={!user ? <Navigate to="/login?tab=register" replace /> : <Navigate to="/" />} />
        <Route path="/auto-login" element={<AutoLogin />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Admin Routes - accessible by admin and worker, never by cross-login tokens */}
        <Route
          path="/admin"
          element={
            user && (userRole === 'admin' || userRole === 'worker') && !sessionStorage.getItem('lima_cross_login') ? (
              <AdminLayout />
            ) : (
              user ? <GoToClientPortal /> : <Navigate to="/login" replace />
            )
          }
        >
          {/* The Dashboard is Dashboard V2; /admin lands on it. The original one is
              kept at /admin/dashboard-legacy for anyone with a direct link. */}
          <Route index element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="dashboard-v2">
              <DashboardV2 />
            </ProtectedRoute>
          } />
          <Route path="dashboard-legacy" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="dashboard">
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="dashboard-v2/shipment/:shipmentId" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="dashboard-v2">
              <DashboardV2 />
            </ProtectedRoute>
          } />
          <Route path="dashboard-v2" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="dashboard-v2">
              <DashboardV2 />
            </ProtectedRoute>
          } />
          <Route path="permissions" element={
            <ProtectedRoute allowedRoles={['admin']} section="permissions">
              <Permissions />
            </ProtectedRoute>
          } />
          <Route path="updates" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="updates">
              <UpdateManagement />
            </ProtectedRoute>
          } />
          {/* The one Tasks page (what was Tasks V2). The old Tasks page is gone;
              /admin/tasks-v2 links and bookmarks land here. */}
          <Route path="tasks" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="tasks">
              <Tasks />
            </ProtectedRoute>
          } />
          <Route path="tasks-v2" element={<Navigate to="/admin/tasks" replace />} />
          
          {/* Admin-only routes */}
          <Route path="users" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="users">
              <UserManagement />
            </ProtectedRoute>
          } />
          {/* Team Management now lives on the Permissions page; old links land there. */}
          <Route path="workers" element={<Navigate to="/admin/permissions" replace />} />
          <Route path="email" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="email-notifications">
              <EmailNotification />
            </ProtectedRoute>
          } />
          {/* Ports now live on the pricing API's admin panel; old links land there. */}
          <Route path="ports" element={<ExternalRedirect to="https://limaapi.onrender.com/admin/login" />} />
          <Route path="calculator" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="calculator">
              <TasksType><Calculator /></TasksType>
            </ProtectedRoute>
          } />
          <Route path="ticket-hub" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="ticket-hub">
              <TicketHub />
            </ProtectedRoute>
          } />
          <Route path="limasign" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="limasign">
              <LimaSign />
            </ProtectedRoute>
          } />
          <Route path="email-builder" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="email-builder">
              <EmailBuilder />
            </ProtectedRoute>
          } />
          <Route path="email-preview" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']} section="email-preview">
              <TasksType><EmailPreview /></TasksType>
            </ProtectedRoute>
          } />

        </Route>

        {/* The client portal is its own page at /user (built from dealer-app/userapp),
            not a route of this app. Netlify serves it before this app ever loads; this
            route only catches an in-app move to /user and turns it into a real page load. */}
        <Route path="/user/*" element={<GoToClientPortal />} />

        {/* Dealer Routes */}
        <Route
          path="/dealerold"
          element={
            user && userRole === 'dealer' ? (
              <DealerLayout />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<DealerDashboard />} />
          <Route path="track" element={<DealerTrackShipments />} />
          <Route path="book" element={<DealerBookShipment />} />
          <Route path="calculator" element={<DealerCalculator />} />
          <Route path="iframe-builder" element={<DealerIframeBuilder />} />
          <Route path="resources" element={<DealerResources />} />
        </Route>

        {/* Redirect root based on user role */}
        <Route
          path="/"
          element={
            user ? (
              (() => {
                console.log('App.tsx: Root redirect - User role:', userRole, 'User:', user);
                if (userRole === 'admin') {
                  console.log('App.tsx: Redirecting admin to /admin');
                  return <Navigate to="/admin" replace />;
                } else if (userRole === 'client' || userRole === 'agent') {
                  console.log('App.tsx: Redirecting client/agent to /user');
                  window.location.replace('/user/');
                  return null;
                } else if (userRole === 'worker') {
                  console.log('App.tsx: Redirecting worker to /admin');
                  return <Navigate to="/admin" replace />;
                } else if (userRole === 'dealer') {
                  // The dealer portal is its own page at /dealer, not a route of this
                  // app, so it needs a real page load rather than a router redirect.
                  // The previous dealer portal is still reachable at /dealerold.
                  console.log('App.tsx: Redirecting dealer to /dealer');
                  window.location.replace('/dealer');
                  return null;
                } else {
                  console.log('App.tsx: Invalid role:', userRole);
                  return <Typography>Access Denied: Invalid Role ({userRole})</Typography>;
                }
              })()
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;